import DataFileLock from '@streamlinehq/streamlinehq/img/streamline-light/interface-essential/lock-unlock/lock-unlock-1.svg';
import LaptopHelpMessage from '@streamlinehq/streamlinehq/img/streamline-light/interface-essential/help/laptop-help-message.svg';
import ShipmentInTransit from '@streamlinehq/streamlinehq/img/streamline-light/shipping-delivery/shipment/shipment-in-transit.svg';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { FeatureAccessMessageType, FeatureId } from '../../../../../../../common/iam/access/feature-access/types';
import { openModal } from '../../../../../../untidy/redux/modules/modal';
import { getBouLinkStyle } from '../../../../../generic/content/texts/bou-link';
import { useGetFeatureAccessMessageType, useGetLoggedInUser } from '../../../../../generic/hooks';
import { CONTRIBUTE_MODAL_NAME } from '../../../../model/input/contributions/contribution-modal';
import { pickNotAvailableContent } from './pick-content';
import {
  NotAvailableCommonElements,
  NotAvailableContentType,
  NotAvailableIcon,
  NotAvailableSectionContent,
  NotAvailableSectionContentWithFeatureId,
} from './types';
import { RequestAccessOrContact } from '@upright/ui/untidy/text-elements';
import { PaywallRequestAction } from '../../request-access/paywall-request-action';
import { toRequestableKey, useRequestAccessOrganizationId } from '../../request-access/requests';

interface Props {
  featureId: FeatureId;
  contentType: NotAvailableContentType;
  customContent?: NotAvailableCommonElements;
  onContactModalOpen?: () => void;
}

export const NotAvailableContent = (props: Props) => {
  const accessMessageType = useGetFeatureAccessMessageType(props.featureId);
  const user = useGetLoggedInUser();
  const dispatch = useDispatch();
  // With USE_SELF_SERVE_ACCESS, a lens or feature the request catalogue sells is asked for here
  // rather than through the contact form; anything else keeps "contact us".
  const requestOrganizationId = useRequestAccessOrganizationId();
  const requestKey = toRequestableKey(props.featureId);
  const requestAction =
    requestOrganizationId !== undefined && requestKey !== undefined ? (
      <RequestActionSlot>
        <PaywallRequestAction organizationId={requestOrganizationId} target={{ key: requestKey }} />
      </RequestActionSlot>
    ) : null;
  const openContactModal = () => {
    if (props.onContactModalOpen) {
      props.onContactModalOpen();
    }
    dispatch(openModal(CONTRIBUTE_MODAL_NAME, { type: 'OPEN_COMMENT' }));
  };

  const NotIncludedInSubscriptionIcon: NotAvailableIcon = { svgElement: <DataFileLock /> };

  const featureContent: NotAvailableSectionContentWithFeatureId[] = [
    {
      featureId: FeatureId.TIME_TRACKING,
      accessMessageType: FeatureAccessMessageType.SUBSCRIPTION_NEEDED,
      elements: {
        icon: NotIncludedInSubscriptionIcon,
        title: <>You don't currently have access to this feature.</>,
        ingress: (
          <>
            Tracking net impact development over time is only available to users with a subscription to the Upright Net
            Impact Platform.
            {requestAction ?? <StyledRequestAccessOrContact openContactModal={openContactModal} />}
          </>
        ),
      },
    },
    {
      featureId: FeatureId.TIME_TRACKING,
      accessMessageType: FeatureAccessMessageType.CURRENT_SUBCRIPTION_NOT_ENOUGH,
      elements: {
        icon: NotIncludedInSubscriptionIcon,
        title: <>You don't currently have access to this feature.</>,
        ingress: (
          <>
            Tracking net impact development over time is not included in your subscription.{' '}
            {requestAction ?? <DefaultContactText {...{ openContactModal, action: 'upgrade' }} />}
          </>
        ),
      },
    },
    {
      featureId: FeatureId.EU_TAXONOMY,
      accessMessageType: FeatureAccessMessageType.SUBSCRIPTION_NEEDED,
      elements: {
        icon: NotIncludedInSubscriptionIcon,
        title: <>You don't currently have access to this feature.</>,
        ingress: (
          <>
            Screening businesses against EU taxonomy of sustainable activities is only available to users with a
            subscription to the Net Impact Platform.{' '}
            {requestAction ?? <StyledRequestAccessOrContact openContactModal={openContactModal} />}
          </>
        ),
      },
    },
    {
      featureId: FeatureId.EU_TAXONOMY,
      accessMessageType: FeatureAccessMessageType.CURRENT_SUBCRIPTION_NOT_ENOUGH,
      elements: {
        icon: NotIncludedInSubscriptionIcon,
        title: <>You don't currently have access to this feature.</>,
        ingress: (
          <>
            Screening businesses against EU taxonomy of sustainable activities is not included in your subscription.{' '}
            {requestAction ?? <DefaultContactText {...{ openContactModal, action: 'upgrade' }} />}
          </>
        ),
      },
    },
  ];

  const defaultContent: NotAvailableSectionContent[] = [
    {
      accessMessageType: FeatureAccessMessageType.SUBSCRIPTION_NEEDED,
      elements: {
        icon: NotIncludedInSubscriptionIcon,
        title: (
          <>
            <>You don't currently have access to this feature.</>
          </>
        ),
        ingress: (
          <>
            This feature is only available to users with a subscription to the Net Impact Platform.{' '}
            {requestAction ?? <StyledRequestAccessOrContact openContactModal={openContactModal} />}
          </>
        ),
        ingress_loggedIn: (
          <>
            This feature is not included in your subscription.{' '}
            {requestAction ?? <DefaultContactText {...{ openContactModal, action: 'purchase' }} />}
          </>
        ),
      },
    },
    {
      accessMessageType: FeatureAccessMessageType.CURRENT_SUBCRIPTION_NOT_ENOUGH,
      elements: {
        icon: NotIncludedInSubscriptionIcon,
        title: (
          <>
            <>You don't currently have access to this feature.</>
          </>
        ),
        ingress: (
          <>
            This feature is only available to users with a subscription to the Net Impact Platform.{' '}
            {requestAction ?? <StyledRequestAccessOrContact openContactModal={openContactModal} />}
          </>
        ),
        ingress_loggedIn: (
          <>
            This feature is not included in your subscription.{' '}
            {requestAction ?? <DefaultContactText {...{ openContactModal, action: 'upgrade' }} />}
          </>
        ),
      },
    },
    {
      accessMessageType: FeatureAccessMessageType.COMING_SOON,
      elements: {
        icon: { svgElement: <ShipmentInTransit />, size: '2em' },
        title: <>Coming soon.</>,
        ingress: (
          <>
            We'll let you know once this feature is available. If you have questions about this feature, leave a message{' '}
            <ContactFormLink
              onClick={() => {
                openContactModal();
              }}
            >
              here
            </ContactFormLink>
            .
          </>
        ),
      },
    },
    {
      accessMessageType: FeatureAccessMessageType.AVAILABLE,
      elements: {
        icon: { svgElement: <LaptopHelpMessage /> },
        title: <>Oh no!</>,
        ingress: (
          <>
            This feature most likely should be enabled for you, but for some reason is not. Please let us know via the{' '}
            <ContactFormLink
              onClick={() => {
                openContactModal();
              }}
            >
              contact form
            </ContactFormLink>{' '}
            or send us an email to hello@uprightproject.com
          </>
        ),
      },
    },
  ];

  return (
    <>
      {pickNotAvailableContent(
        props.featureId,
        props.contentType,
        featureContent,
        accessMessageType,
        defaultContent,
        user,
        props.customContent,
      )}
    </>
  );
};

const RequestActionSlot = styled.div`
  margin: 1em auto 0;
  max-width: 20em;
  text-align: left;
`;

const ContactFormLink = styled.span`
  ${getBouLinkStyle(true)}
  font-weight: normal;
  text-decoration: underline;
`;

const StyledRequestAccessOrContact = styled(RequestAccessOrContact)`
  margin-top: 2em;
  a {
    font-weight: normal;
  }
`;

const DefaultContactText = (props: { action: 'purchase' | 'upgrade'; openContactModal: () => void }) => {
  const actionText = (() => {
    switch (props.action) {
      case 'purchase':
        return 'to purchase subscription.';
      case 'upgrade':
        return 'to upgrade your subscription.';
    }
  })();
  return (
    <>
      <br />
      <br />
      <ContactFormLink
        onClick={() => {
          props.openContactModal();
        }}
      >
        Contact us
      </ContactFormLink>{' '}
      {actionText}
    </>
  );
};
